import React, { ChangeEvent, Component, InputHTMLAttributes, KeyboardEvent, ReactElement } from "react";
import FormField from "./WebFormField";
import { isArabicChar, isEnglishChar } from "@my-dld/utils/UIUtils";
import { WebFormFieldProps } from "./index";
import { readError, readValue, writeValue } from "@eres/forms/Utils";
import { getValidationClass } from "./Utils";
import { DefaultProps } from "@eres/forms";

export interface Props<XT = string> extends WebFormFieldProps<string, XT> {
	inputClassName?: string;
	errorInputClass?: string;
	placeHolder?: string;
	customInput?: ReactElement;
	type?: string;
	cursorPositioner?: ( previousValue: string | null, previousPosition: number, value: string ) => number;
	maxLength?: number;
	inputProps?: InputHTMLAttributes<HTMLInputElement>;
	mode?: "input" | "text";
	suffix?: string;
	textRenderer?: ( props: Props<XT>, value?: string ) => ReactElement;
	inputRenderer?: ( props: Props<XT>, inpProps: any ) => ReactElement;
}


interface State {
	isFocus: boolean;
}

class TextInputField<XT = string> extends Component<Props<XT>, State> {
	static defaultProps: DefaultProps<Props<any>> = {
		inputClassName: "form-control ltr-text-field",
		errorInputClass: "form-input-error is-invalid",
		type: "text",
		mode: "input",
	};

	constructor( props: Props<XT> ) {
		super( props );
		this.state = {
			isFocus: false,
		};
	}

	onKeyUp = ( e: KeyboardEvent<HTMLInputElement> ) => {
		let inp = e.target as HTMLInputElement;
		let v = inp.value;
		inp.value = "" + v;
	};

	onChange = ( e: ChangeEvent<HTMLInputElement> ) => {
		const target = e.target;
		const rVal = readValue( this.props );
		let pVal = rVal ? "" + rVal : "";
		let cVal = target.value;
		if ( cVal === "" && e.target.checkValidity ) {
			if ( !e.target.checkValidity() ) {
				cVal = pVal;
			}
		}
		if ( this.props.inputProps && this.props.inputProps.type === "number" ) {
			cVal = cVal.replace( /[^\d]/g, "" );
		}
		if ( cVal !== "" ) {
			if ( this.props.type === "english" ) {
				cVal = isEnglishChar( cVal ) ? cVal : ( pVal ? pVal : "" );
			} else if ( this.props.type === "arabic" ) {
				cVal = isArabicChar( cVal ) ? cVal : ( pVal ? pVal : "" );
			}
		}
		let maxLength = this.props.maxLength;
		if ( maxLength !== undefined && maxLength != -1 && cVal.length > maxLength ) {
			cVal = cVal.slice( 0, maxLength );
		}
		let pc = target.selectionStart;
		if ( this.props.onValueChange ) {
			this.props.onValueChange( cVal );
		}
		writeValue( this.props, cVal, () => {
			if ( this.props.onValueUpdated ) {
				this.props.onValueUpdated( cVal );
			}
			if ( this.props.cursorPositioner ) {
				let selectionStart = this.props.cursorPositioner( pVal, pc ? pc : 0, cVal );
				target.selectionStart = selectionStart;
				target.selectionEnd = selectionStart;
			}
		} );
	};
	isEditable = () => {
		if ( this.props.disabled ) return false;
		if ( this.props.enableEditProvider )
			return !this.props.enableEditProvider( this.props );
		return true;
	};

	render() {
		const {
			id, inputClassName, placeHolder, error, errorInputClass, customInput, type, disabled, extraInputClassName,
			inputProps, required, suffix, inputRenderer
		} = this.props;
		let xError = error;
		if ( error == null )
			xError = readError( this.props );

		let cValue = readValue( this.props );

		let inputStyle = getValidationClass( this.state.isFocus, this.props, cValue, xError );
		let iClass = inputClassName + ( extraInputClassName ? ` ${ extraInputClassName }` : "" ) +
			inputStyle + ( error ? ` ${ errorInputClass }` : "" );
		const inpProps = {
			id: id,
			value: cValue === undefined ? "" : cValue as string,
			onChange: this.onChange,
			onKeyUp: this.onKeyUp,
			className: iClass,
			placeholder: placeHolder,
			type: type,
			disabled: !this.isEditable(),
			onFocus: this.onFocus,
			onBlur: this.onBlur,
			...( inputProps ),
		};
		if ( inpProps.value == null ) inpProps.value = "";
		let ele = inputRenderer ? inputRenderer( this.props, inpProps ) : <input { ...inpProps }/>;
		if ( suffix ) {
			ele = <div className={ "input-group" }>
				{ ele }
				<div className="input-group-append">
					<span className="input-group-text">{ suffix }</span>
				</div>
			</div>;
		}
		if ( customInput ) {
			ele = React.cloneElement( this.props.customInput as React.ReactElement<any>, inpProps );
		} else if ( this.props.mode === "text" ) {
			if ( this.props.textRenderer ) {
				ele = this.props.textRenderer( this.props, inpProps.value as string );
			} else
				ele = <div className={ "form-text-container" } id={ this.props.id }>
					<span className={ "form-text-data" }>{ inpProps.value }</span>
				</div>;
		}
		return (
			<FormField { ...this.props }>
				{ ele }
				{ this.props.children }
			</FormField>
		);
	}

	private onFocus = () => {
		this.setState( { isFocus: true } );
	};

	private onBlur = () => {
		this.setState( { isFocus: false } );
	};
}


export default TextInputField;
